import type { CustomInvoiceLineItem } from './api';

/**
 * The three maps populated from the download-urls polling response
 * (BreakdownDialog/index.tsx), keyed by billing_invoice_id. Shared between the
 * BreakdownDialog persisted CSV export and the PMS export writers (pmsExport.ts) so
 * both agree on when an invoice has persisted invoice data and what its total is.
 */
export type CustomInvoiceMaps = {
  customInvoiceAmountCents: Map<string, number>;
  customInvoiceBreakdown: Map<
    string,
    { usageCents: number; commonAreaCents: number; feeCents: number }
  >;
  customInvoiceLineItems: Map<string, CustomInvoiceLineItem[]>;
};

/**
 * A custom invoice has "persisted invoice data" once the download-urls response has
 * returned at least one of amount_due_cents / the per-category breakdown / the frozen
 * line items for it. Presence of persisted data for ANY invoice on a bill means custom
 * invoices exist for that bill: persisted values become the source of truth for every
 * invoiced unit's row (CSV export rows, PMS export rows). When none of the bill's
 * invoices carry persisted data (no custom invoices generated yet), callers fall back
 * to the live-compute path.
 */
export function hasPersistedInvoiceData(
  invoiceId: string | null | undefined,
  maps: CustomInvoiceMaps,
): boolean {
  return (
    !!invoiceId &&
    (maps.customInvoiceAmountCents.has(invoiceId) ||
      maps.customInvoiceBreakdown.has(invoiceId) ||
      maps.customInvoiceLineItems.has(invoiceId))
  );
}

/** Minimal unit shape resolveInvoiceTotalDollars needs to compute the live-compute
 * fallback total (un-prorated unit total: water usage + estimated usage + fees). */
export type UnitCostFields = {
  unit_cost_meter?: number;
  unit_cost?: number;
  unit_estimated_cost?: number;
};

/**
 * Resolve the dollar total for one invoice: the persisted amount_due_cents when
 * present, otherwise the live-computed un-prorated unit total (same fallback the
 * BreakdownDialog CSV export uses in buildPersistedRow).
 */
export function resolveInvoiceTotalDollars(
  unit: UnitCostFields,
  invoiceId: string,
  customInvoiceAmountCents: Map<string, number>,
): number {
  const computedInvoiceAmount =
    (unit.unit_cost_meter ?? 0) + (unit.unit_estimated_cost ?? 0) + (unit.unit_cost ?? 0);
  const persistedCents = customInvoiceAmountCents.get(invoiceId);
  return persistedCents !== undefined ? persistedCents / 100 : computedInvoiceAmount;
}
